import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { getMostFrequentShoppingHour } from '../../utils/timeAnalytics';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import ProgressDashes from '../../components/ProgressDashes';
import { StoryNavigation } from '../../components/StoryNavigation';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';

const FavoriteTimePage = () => {
  const navigate = useNavigate();
  const transactions = useSelector((state: RootState) => state.transactions.items);
  const timeStats = useMemo(() => getMostFrequentShoppingHour(transactions), [transactions]);
  const { goToNext, goToPrev, hasNext, hasPrev } = useStoryNavigation();

  // Auto-navigate to the next page after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/insights/spending-categories');
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Format hour to always be 2 digits
  const formatHour = (hour: number) => {
    return hour.toString().padStart(2, '0');
  };

  return (
    <div className="min-h-screen bg-[#221A51]/[0.07] relative overflow-hidden">
      <StoryNavigation
        onNext={goToNext}
        onPrev={goToPrev}
        hasNext={hasNext}
        hasPrev={hasPrev}
      />
      <div className="px-4 sm:px-16 pt-16 sm:pt-20 max-w-4xl mx-auto">
        {/* Progress dashes */}
        <ProgressDashes currentStep={5} totalSteps={11} />

        <div className="px-2 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-1 sm:space-y-2"
          >
            <h1 className="text-2xl sm:text-[34px] leading-[140%] font-black text-[#221A51]">
              Favourite Time
              <br />
              to Shop
            </h1>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 sm:mt-12"
          >
            {/* Digital Clock Display */}
            <div className="bg-[#221A51] rounded-xl p-6 sm:p-12 border-2 border-[#221A51] shadow-[4px_4px_0px_0px_#9B86EA] relative">
              {/* Snooze Button */}
              <div className="absolute -top-3 right-4 sm:right-8 bg-white px-3 sm:px-4 py-1 rounded-md border-2 border-[#221A51] text-[#221A51] text-xs sm:text-sm font-bold shadow-[2px_2px_0px_0px_#9B86EA]">
                SNOOZE
              </div>
              
              <div className="bg-[#1A1425] p-8 sm:p-12 rounded-lg">
                <div className="flex items-baseline justify-center space-x-2 sm:space-x-4">
                  {/* Time Display */}
                  <div className="font-mono text-7xl sm:text-[168px] font-black text-[#9BFFA9] tracking-wider tabular-nums leading-none">
                    {formatHour(timeStats.hour)}:00
                  </div>
                  {/* AM/PM Indicator */}
                  <div className="text-[#9BFFA9] text-3xl sm:text-6xl font-bold">
                    {timeStats.ampm}
                  </div>
                </div>
              </div>

              {/* Stats Display */}
              <div className="mt-8 sm:mt-10 space-y-3 sm:space-y-4 text-center">
                <div className="text-white/80 text-base sm:text-lg">
                  <span className="text-white font-bold">{timeStats.count} purchases</span> around this time
                </div>
                <div className="text-white/80 text-base sm:text-lg">
                  Average spend: <span className="text-white font-bold">{formatCurrency(timeStats.averageAmount)}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Decorative stars */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="fixed bottom-0 right-0 w-48 h-48 opacity-20 -z-10"
        >
          <img src="/stars.svg" alt="" className="w-full h-full" />
        </motion.div>
      </div>
    </div>
  );
};

export default FavoriteTimePage;
